<template>
  <div class="template-manager-panel">
    <section class="panel-header">
      <div>
        <h3>接口模板管理</h3>
        <p>维护 funcEx 路径、功能号和默认 Parm，保存后模拟请求页立即可用</p>
      </div>

      <el-tag effect="plain" type="success">{{ enabledCount }} 个启用</el-tag>
    </section>

    <div class="manager-layout">
      <section class="config-section template-list-section">
        <div class="section-heading">
          <div>
            <h4>模板列表</h4>
            <p>内置模板可覆盖或禁用，自定义模板可删除</p>
          </div>
          <div class="heading-actions">
            <el-button :icon="Refresh" plain @click="refreshTemplates">刷新</el-button>
            <el-button :icon="Plus" type="primary" @click="resetEditor">新增</el-button>
          </div>
        </div>

        <el-table :data="templates" border size="small" class="template-table">
          <el-table-column prop="funcType" label="功能号" width="76" align="center" />
          <el-table-column label="模板" min-width="220">
            <template #default="{ row }">
              <div class="template-cell">
                <strong>{{ row.title }}</strong>
                <small>{{ row.funcPath }}</small>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="来源" width="96" align="center">
            <template #default="{ row }">
              <el-tag :type="row.isCustom ? 'warning' : 'info'" effect="plain">
                {{ row.isCustom ? '自定义' : '内置' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="启用" width="86" align="center">
            <template #default="{ row }">
              <el-switch
                v-model="row.enabled"
                :active-value="true"
                :inactive-value="false"
                @change="handleToggle(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="138" align="center">
            <template #default="{ row }">
              <el-button :icon="Edit" plain circle size="small" @click="loadEditor(row)" />
              <el-popconfirm
                v-if="row.isCustom"
                title="删除自定义配置？内置模板会恢复默认值。"
                confirm-button-text="删除"
                cancel-button-text="取消"
                @confirm="handleDelete(row.key)"
              >
                <template #reference>
                  <el-button :icon="Delete" type="danger" plain circle size="small" />
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section class="config-section editor-section">
        <div class="section-heading">
          <div>
            <h4>{{ editingKey ? '编辑模板' : '新增模板' }}</h4>
            <p>key 保存后用于模拟请求页下拉列表</p>
          </div>
          <el-tag effect="plain">{{ editor.enabled ? '启用' : '禁用' }}</el-tag>
        </div>

        <el-form label-position="top" class="editor-form">
          <div class="field-grid">
            <el-form-item label="key">
              <el-input v-model.trim="editor.key" placeholder="newFunc33" clearable />
            </el-form-item>

            <el-form-item label="名称">
              <el-input v-model.trim="editor.title" placeholder="新增 33 功能" clearable />
            </el-form-item>

            <el-form-item label="funcType">
              <el-input-number
                v-model="editor.funcType"
                :min="1"
                :max="9999"
                class="full-control"
                controls-position="right"
              />
            </el-form-item>

            <el-form-item label="funcPath">
              <el-input v-model.trim="editor.funcPath" placeholder="NewFunc33" clearable />
            </el-form-item>

            <el-form-item label="method">
              <el-select v-model="editor.method" class="full-control">
                <el-option label="POST" value="POST" />
              </el-select>
            </el-form-item>

            <el-form-item label="状态">
              <el-switch
                v-model="editor.enabled"
                inline-prompt
                active-text="启用"
                inactive-text="禁用"
              />
            </el-form-item>
          </div>

          <el-form-item label="说明">
            <el-input v-model.trim="editor.desc" placeholder="funcEx/NewFunc33" clearable />
          </el-form-item>

          <el-form-item label="默认 Parm JSON">
            <el-input
              v-model="parmText"
              type="textarea"
              :rows="8"
              resize="vertical"
              class="json-input"
              spellcheck="false"
            />
          </el-form-item>

          <div class="form-actions">
            <el-button plain @click="resetEditor">清空</el-button>
            <el-button :icon="DocumentChecked" plain @click="formatParm">格式化 JSON</el-button>
            <el-button :icon="Select" type="primary" @click="saveEditor">保存模板</el-button>
          </div>
        </el-form>
      </section>
    </div>

    <section class="config-section import-section">
      <div class="section-heading">
        <div>
          <h4>自定义模板 JSON</h4>
          <p>用于备份或迁移浏览器本地模板配置</p>
        </div>
        <div class="heading-actions">
          <el-button :icon="DocumentCopy" plain @click="exportCustomTemplates">导出</el-button>
          <el-button :icon="Upload" type="primary" plain @click="importCustomTemplates">导入覆盖</el-button>
        </div>
      </div>

      <el-input
        v-model="bulkJson"
        type="textarea"
        :rows="7"
        resize="vertical"
        class="json-input"
        spellcheck="false"
        placeholder="导出后这里会显示自定义模板 JSON，也可以粘贴数组后导入覆盖"
      />
    </section>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  Delete,
  DocumentChecked,
  DocumentCopy,
  Edit,
  Plus,
  Refresh,
  Select,
  Upload,
} from '@element-plus/icons-vue';

import {
  deleteCustomSimulatorTemplate,
  listCustomSimulatorTemplates,
  listSimulatorTemplates,
  normalizeJsonText,
  normalizeSimulatorTemplate,
  notifySimulatorTemplatesChanged,
  saveCustomSimulatorTemplates,
  upsertSimulatorTemplate,
} from '@/utils/fordDiagnosticSimulatorUi.mjs';

const templates = ref(listAllTemplates());
const bulkJson = ref('');
const editingKey = ref('');
const parmText = ref('{}');

const editor = reactive({
  key: '',
  title: '',
  desc: '',
  funcType: 33,
  funcPath: '',
  method: 'POST',
  enabled: true,
});

const enabledCount = computed(() => {
  return templates.value.filter((template) => template.enabled !== false).length;
});

function listAllTemplates() {
  return listSimulatorTemplates({ includeDisabled: true });
}

function refreshTemplates() {
  templates.value = listAllTemplates();
}

function resetEditor() {
  editingKey.value = '';
  editor.key = '';
  editor.title = '';
  editor.desc = '';
  editor.funcType = 33;
  editor.funcPath = '';
  editor.method = 'POST';
  editor.enabled = true;
  parmText.value = '{}';
}

function loadEditor(template) {
  editingKey.value = template.key;
  editor.key = template.key;
  editor.title = template.title;
  editor.desc = template.desc;
  editor.funcType = template.funcType;
  editor.funcPath = template.funcPath;
  editor.method = template.method || 'POST';
  editor.enabled = template.enabled !== false;
  parmText.value = JSON.stringify(template.parm || {}, null, 2);
}

function buildEditorTemplate() {
  const parm = JSON.parse(normalizeJsonText(parmText.value));
  if (!parm || typeof parm !== 'object' || Array.isArray(parm)) {
    throw new Error('默认 Parm 必须是 JSON 对象');
  }

  return normalizeSimulatorTemplate({
    ...editor,
    parm,
  });
}

function saveEditor() {
  try {
    const template = buildEditorTemplate();
    upsertSimulatorTemplate(template);
    refreshTemplates();
    notifySimulatorTemplatesChanged();
    loadEditor(template);
    ElMessage.success('模板已保存');
  } catch (error) {
    ElMessage.warning(error.message || '保存失败');
  }
}

function formatParm() {
  try {
    parmText.value = normalizeJsonText(parmText.value);
    ElMessage.success('JSON 已格式化');
  } catch (error) {
    ElMessage.warning(error.message);
  }
}

function handleToggle(template) {
  try {
    upsertSimulatorTemplate({
      ...template,
      enabled: template.enabled !== false,
    });
    refreshTemplates();
    notifySimulatorTemplatesChanged();
    ElMessage.success(template.enabled ? '模板已启用' : '模板已禁用');
  } catch (error) {
    ElMessage.warning(error.message || '状态保存失败');
    refreshTemplates();
  }
}

function handleDelete(key) {
  deleteCustomSimulatorTemplate(key);
  refreshTemplates();
  notifySimulatorTemplatesChanged();
  if (editingKey.value === key) {
    resetEditor();
  }
  ElMessage.success('自定义配置已删除');
}

function exportCustomTemplates() {
  bulkJson.value = JSON.stringify(listCustomSimulatorTemplates(), null, 2);
  ElMessage.success('自定义模板已导出到文本框');
}

function importCustomTemplates() {
  try {
    const parsed = JSON.parse(bulkJson.value || '[]');
    if (!Array.isArray(parsed)) {
      throw new Error('导入内容必须是模板数组');
    }

    saveCustomSimulatorTemplates(parsed);
    refreshTemplates();
    notifySimulatorTemplatesChanged();
    ElMessage.success('自定义模板已导入');
  } catch (error) {
    ElMessage.warning(error.message || '导入失败');
  }
}

refreshTemplates();
</script>

<style scoped>
.template-manager-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header,
.config-section {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header h3 {
  font-size: 18px;
}

.panel-header p,
.section-heading p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.45;
}

.manager-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(360px, 0.9fr);
  gap: 14px;
  align-items: start;
}

.config-section {
  min-width: 0;
  padding: 16px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.heading-actions,
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.template-table {
  width: 100%;
}

.template-table :deep(.el-table__header th) {
  background: #f6f9fc !important;
  color: #4b5f75;
  font-weight: 800;
}

.template-cell {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.template-cell strong,
.template-cell small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-cell small {
  color: #7a8a9d;
  font-family: Consolas, Monaco, monospace;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 12px;
}

.full-control {
  width: 100%;
}

.editor-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.json-input :deep(textarea) {
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  line-height: 1.45;
}

@media (max-width: 1180px) {
  .manager-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .panel-header,
  .section-heading {
    flex-direction: column;
  }

  .field-grid {
    grid-template-columns: 1fr;
  }

  .heading-actions,
  .form-actions {
    justify-content: flex-start;
  }
}
</style>
